import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from 'util/mobx/stores';
import ArrowDotsVerticalIcon from 'components/animationIcons/ArrowDotsVerticalIcon';
import ArrowIcon from 'components/ArrowIcon';
import ImageCircle from 'components/ImageCircle';
import ItemMenu from 'components/ItemMenu';
import ItemMenuButton from 'components/ItemMenuButton';
import Popover from 'components/Popover';
import './BookListItemBody.css';
import { Can, subjectArea, actions } from 'casl/setupCaslAbility';
import { GuideDTO } from 'dto/guide';
import ValueTag from 'components/ValueTag';
import { subject } from '@casl/ability';

interface Props {
  guide: GuideDTO;
  hover?: boolean;
}

const BookListItemBody = observer((props: Props) => {
  const { guideStore, domainStore } = useStores();
  const { t } = useTranslation('guide');

  /**
   * wrapper for the button clicks which closes the dialog and stops the click event
   * @param event
   * @param handler
   */
  const menuButtonClickHandlerWrapper = (event: React.MouseEvent, handler: (event: React.MouseEvent) => void) => {
    // setIsOpen(false);
    handler(event);
    event.preventDefault();
  };

  const handlerCopyGuide = (event: React.MouseEvent, handler: (event: React.MouseEvent) => void) => {
    handler(event);
    event.preventDefault();
  };

  return (
    <>
      <ImageCircle builtinPictureName="icon_book" highlight={!!props.hover} />
      <div className="book_info book_hover_blue">
        <div>{props.guide.name}</div>
        <div className="btn_show_detail btn-detail-position">
          <ValueTag title={t('guideId')} value={props.guide.guideNumber.toString()} />
          {!guideStore.guideMaterialsToClone && !guideStore.guideProcedureToClone && (
            <Popover trigger={<ArrowDotsVerticalIcon direction={!props.hover} />}>
              {({ handleClose, isOpen }) => (
                <>
                  <ItemMenu isOpen={!!isOpen} handleClose={handleClose}>
                    <Can I={actions.add} this={subject(subjectArea.guide, { departmentId: domainStore.currentDepartment.id })}>
                      <ItemMenuButton
                        label={t('button.duplicateGuide')}
                        icon="icon_single-copy-04_16.svg"
                        isInPopover
                        handleClick={event =>
                          handlerCopyGuide(event, () => {
                            guideStore.cloneGuide({ sourceGuideId: props.guide.guideId });
                          })
                        }
                      />
                      <ItemMenuButton
                        label={t('button.renameGuide')}
                        icon="icon_background_16.svg"
                        isInPopover
                        handleClick={event =>
                          menuButtonClickHandlerWrapper(event, () => {
                            guideStore.setSelectedGuide(props.guide);
                            guideStore.setShowGuideFlyout(true);
                          })
                        }
                      />
                    </Can>
                    <Can I={actions.delete} this={subject(subjectArea.guide, { departmentId: domainStore.currentDepartment.id })}>
                      <ItemMenuButton
                        label={t('button.deleteGuide')}
                        icon="icon_bin_16.svg"
                        warning
                        isInPopover
                        handleClick={event =>
                          menuButtonClickHandlerWrapper(event, () => {
                            guideStore.setSelectedGuide(props.guide);
                            guideStore.showDeleteGuideFlyout(props.guide.guideId);
                          })
                        }
                      />
                    </Can>
                  </ItemMenu>
                </>
              )}
            </Popover>
          )}

          {(guideStore.guideMaterialsToClone || guideStore.guideProcedureToClone) && <ArrowIcon hover={props.hover} />}
        </div>
      </div>
    </>
  );
});

export default BookListItemBody;
